/**
 * ExpressCustomerDialog — §58 PR 4: put a hauler on the EXPRESS template with its ONE customer.
 *
 * Opened from the Plan & Features tab (§58 PR 2): "Apply template…" with EXPRESS chosen, or
 * "Change customer…" for a hauler already on EXPRESS. Paelode searches the
 * platform catalog (the same operator search ApproveDialog uses), picks the operator, and sees
 * what the change affects before saving: the hauler's other customers are frozen for new work
 * (their open jobs, tickets and invoices finish normally; nothing is deactivated), and a customer
 * row is created for the chosen operator when the hauler has none (it needs the 2–4 letter
 * abbreviation that goes on every run-ticket number). A reason is required; the server writes
 * the audit row in the same transaction.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { ApiError, apiErrorText, useDebouncedValue } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle } from '../../ui/bits';
import {
  usePlatformOperatorSearch,
  type PlatformOperatorSearchRow,
} from '../operators/platform-operator-search';
import { useExpressPreview, useSetExpressCustomer } from './express-hooks';
import { ABBREVIATION_RE, ABBREVIATION_RULE_TEXT } from '@lodex-pro/shared/abbreviation';

export interface ExpressCustomerDialogProps {
  readonly open: boolean;
  readonly tenantId: string;
  /** The current Express customer, when the hauler is already on EXPRESS. */
  readonly currentOperator: { readonly id: string; readonly name: string } | null;
  /** Carried from "Apply template…": also clear the hauler's feature overrides. */
  readonly clearOverrides?: boolean;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

/** What the change affects, in plain words (no plan names: §58). */
export function expressImpactText(otherCustomers: number, openJobs: number): string {
  if (otherCustomers === 0) return 'This is their only customer.';
  const customers = `${String(otherCustomers)} other customer${otherCustomers === 1 ? '' : 's'}`;
  const jobs = `${String(openJobs)} open job${openJobs === 1 ? '' : 's'}`;
  return `${customers} will take no new sites, wells or jobs. Their ${jobs}, tickets and invoices finish normally; nothing is deactivated.`;
}

export function ExpressCustomerDialog({
  open,
  tenantId,
  currentOperator,
  clearOverrides = false,
  onClose,
  onSaved,
}: ExpressCustomerDialogProps): React.JSX.Element {
  const [picked, setPicked] = React.useState<{ id: string; label: string } | null>(null);
  const [query, setQuery] = React.useState('');
  const [abbreviation, setAbbreviation] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const debounced = useDebouncedValue(query, 300);
  const search = usePlatformOperatorSearch(debounced);
  const preview = useExpressPreview(tenantId, picked?.id ?? null);

  React.useEffect(() => {
    if (!open) return;
    setPicked(
      currentOperator === null ? null : { id: currentOperator.id, label: currentOperator.name },
    );
    setQuery('');
    setAbbreviation('');
    setReason('');
    setError(null);
  }, [open, currentOperator]);

  const save = useSetExpressCustomer(tenantId, (r) => {
    toast.success(`Now works for one customer: ${r.expressOperatorName}.`);
    onSaved();
    onClose();
  });

  const needsAbbreviation = preview.data?.abbreviationRequired === true;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (picked === null) {
      setError('Choose the customer from the catalog.');
      return;
    }
    if (needsAbbreviation && !ABBREVIATION_RE.test(abbreviation.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    if (reason.trim() === '') {
      setError('A reason is required.');
      return;
    }
    save.mutate(
      {
        operatorId: picked.id,
        reason: reason.trim(),
        ...(clearOverrides && { clearOverrides: true }),
        ...(needsAbbreviation && { abbreviation: abbreviation.trim().toUpperCase() }),
      },
      {
        onError: (err: Error) => {
          // The reason is in the body's fields; err.message is only "API 409: path" (OPEN-199).
          setError(
            err instanceof ApiError && err.fields.some((f) => f.reason === 'abbreviation_taken')
              ? 'That abbreviation is already used by another of their customers.'
              : apiErrorText(err, 'Could not save. Check the customer and try again.'),
          );
        },
      },
    );
  }

  const results: readonly PlatformOperatorSearchRow[] = search.data?.operators ?? [];
  const showResults = picked === null && debounced.trim().length > 0 && results.length > 0;

  return (
    <Dialog open={open} onOpenChange={(o): void => (o ? undefined : onClose())}>
      <DialogContent style={{ maxWidth: '540px' }}>
        <DialogHeader>
          <DialogTitle>One customer (Express)</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <FormRow
            label="Customer"
            htmlFor="expressOperatorSearch"
            required
            hint="A platform catalog operator. The hauler can only add sites, wells and jobs for it."
          >
            {picked !== null ? (
              <div
                aria-label="Selected customer"
                style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}
              >
                <span>{picked.label}</span>
                <button
                  type="button"
                  onClick={(): void => setPicked(null)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  id="expressOperatorSearch"
                  type="text"
                  style={fieldStyle}
                  value={query}
                  onChange={(e): void => setQuery(e.target.value)}
                  placeholder="Search the catalog by name…"
                  autoComplete="off"
                />
                {showResults && (
                  <ul
                    role="listbox"
                    aria-label="Catalog matches"
                    style={{ listStyle: 'none', padding: 0 }}
                  >
                    {results.slice(0, 20).map((op) => {
                      const name = op.displayName ?? op.name;
                      return (
                        <li key={op.id}>
                          <button
                            type="button"
                            onClick={(): void => setPicked({ id: op.id, label: name })}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                          >
                            {`${name} (${op.state ?? '—'})`}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </FormRow>

          {preview.data !== undefined && (
            <p
              data-testid="express-impact"
              style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: '8px 0' }}
            >
              {expressImpactText(preview.data.otherCustomers, preview.data.openJobs)}
            </p>
          )}

          {needsAbbreviation && (
            <FormRow
              label="Abbreviation"
              htmlFor="expressAbbreviation"
              required
              hint="Letters only (A–Z), 2 to 4 of them. They have no customer row for this operator yet; it goes in front of every run-ticket number."
            >
              <input
                id="expressAbbreviation"
                type="text"
                style={fieldStyle}
                value={abbreviation}
                onChange={(e): void => setAbbreviation(e.target.value)}
                maxLength={4}
              />
            </FormRow>
          )}

          <FormRow label="Reason" htmlFor="expressReason" required>
            <input
              className="keep-case"
              id="expressReason"
              type="text"
              style={fieldStyle}
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
            />
          </FormRow>

          {error !== null && (
            <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '13px' }}>
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
